/**
 * Centralized Mutation-to-Query Invalidation Map
 *
 * This module defines the relationship between mutations and the queries they should
 * invalidate. When a mutation succeeds, the MutationCache will automatically invalidate
 * the corresponding queries defined in this map.
 *
 * ## Why use this pattern?
 *
 * 1. **Centralized logic**: All invalidation rules are in one place, making it easy to
 *    understand which mutations affect which queries.
 *
 * 2. **Consistency**: Ensures cache invalidation happens regardless of where the mutation
 *    is called (hooks, imperative calls, etc.).
 *
 * 3. **Type safety**: Uses Orval-generated query key factories to ensure correct keys.
 *
 * 4. **Discoverability**: New developers can quickly understand data dependencies.
 *
 * ## How to add a new invalidation rule:
 *
 * 1. Import the query key factory from '@capture/client-api/src/orval'
 *    Example: `getGetRequestJobsInfoByJobUuidQueryKey`
 *
 * 2. Add the base key constant and entry to MUTATION_INVALIDATION_MAP
 *
 * ## Query Key Structure (Orval convention):
 *
 * - Query keys follow the pattern: `['/endpoint_path', ...params]`
 *   Example: getGetRequestAccountInfoQueryKey() returns `['/account_info', params?]`
 *
 * - Use `queryKey[0]` to match the base path when you need to invalidate
 *   regardless of params (e.g., invalidate all account_info queries)
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/mutations#mutation-side-effects
 * @see https://tanstack.com/query/latest/docs/reference/MutationCache
 */

import type { QueryClient } from '@tanstack/react-query'
import type { CollectionClientEventData, MemoryClientEventData } from '~/@types/backend-types'

import {
    type ClientEventRequestDTO,
    ClientEventTypeDTO,
    getGetRequestAccountInfoQueryKey,
    getGetRequestCardsQueryKey,
    getGetRequestMemoryCollectionsByCollectionUuidQueryKey,
    type PostRequestClientEventParams,
    type PostRequestNameParams,
} from '@capture/client-api/src/orval'

import { getQueryMemoryByDateRangeOptions } from './app.queries'

/**
 * Type definition for invalidation handler functions.
 * Each handler receives the QueryClient and optionally the mutation variables.
 * The variables parameter allows handlers to perform context-aware invalidation.
 */
type InvalidationHandler<TVariables = unknown> = (
    queryClient: QueryClient,
    variables: TVariables,
) => void | Promise<void>

type MutationInvalidationMap = {
    postRequestName: InvalidationHandler<{ params: PostRequestNameParams }>
    postRequestClientEvent: InvalidationHandler<{
        data: ClientEventRequestDTO
        params: PostRequestClientEventParams
    }>
}

/**
 * Map of mutation keys to their corresponding query invalidation handlers.
 *
 * The key is the mutation key (first element of the mutationKey array, as generated by Orval).
 * The value is a function that performs the necessary query invalidations.
 */
export const MUTATION_INVALIDATION_MAP: MutationInvalidationMap = {
    /**
     * Profile Name Mutation → Account Info Query
     *
     * When the user's profile name is updated via POST /name,
     * invalidate the account_info query so that:
     * - useAccountInfo() hook refetches with new name
     * - queryAccountInfoWithCache() returns fresh data
     */
    postRequestName: (queryClient) => {
        void queryClient.invalidateQueries({
            predicate: (query) => query.queryKey[0] === getGetRequestAccountInfoQueryKey()[0],
        })
    },

    /**
     * Client Event Mutation → Collection/Memory Queries
     *
     * When a client event is posted (viewed_collection_image or viewed_memory_image),
     * invalidate the relevant queries based on the event type.
     *
     * This handler inspects the event type in the variables to determine
     * which queries to invalidate.
     */
    postRequestClientEvent: async (queryClient, variables) => {
        const event = variables.data.events[0]
        const commonParams = variables.params

        switch (event.event_type) {
            case ClientEventTypeDTO.viewed_collection_image: {
                const eventData = event.data as CollectionClientEventData
                await queryClient.invalidateQueries({
                    predicate: (query) =>
                        query.queryKey[0] ===
                        getGetRequestMemoryCollectionsByCollectionUuidQueryKey(
                            eventData.collection_uuid,
                            commonParams,
                        )[0],
                })
                break
            }
            case ClientEventTypeDTO.viewed_memory_image: {
                const eventData = event.data as MemoryClientEventData
                const { queryKey: queryMemoryByDateRangeKey } =
                    await getQueryMemoryByDateRangeOptions(eventData.date_range)
                await queryClient.invalidateQueries({
                    predicate: (query) => query.queryKey[0] === queryMemoryByDateRangeKey[0],
                })
                break
            }
        }

        await queryClient.invalidateQueries({
            predicate: (query) => query.queryKey[0] === getGetRequestCardsQueryKey()[0],
        })
    },
}

/**
 * Get the invalidation handler for a given mutation key.
 *
 * @param mutationKey - The mutation key array from the mutation options
 * @returns The invalidation handler if one exists, undefined otherwise
 */
export function getInvalidationHandler(
    mutationKey: readonly unknown[] | undefined,
): InvalidationHandler | undefined {
    if (!mutationKey || mutationKey.length === 0) {
        return undefined
    }

    const key = mutationKey[0]
    if (typeof key !== 'string') {
        return undefined
    }

    return MUTATION_INVALIDATION_MAP[key as keyof MutationInvalidationMap] as InvalidationHandler
}
